import { ColumnType } from '../types';
import { TableViewState } from './types';
import {
  effectivePinnable, isGroup, isSystem, stateKeyOf,
} from './columns';
import { devWarn, isDevEnv } from './env';

/**
 * Development-only config sanity checks: duplicate raw keys, `settingsKey`
 * aliases that span more than one group, and sub-groups nested beyond the two
 * levels the resolver resolves. Never throws; a no-op in production — the
 * guard skips the recursive walks entirely, not just their output.
 */
export function warnColumnInvariants<R>(columns: ColumnType<R>[]): void {
  if (!isDevEnv()) return;
  warnDuplicateKeys(columns);
  warnSettingsKeyCollisions(columns);
  warnDeepNesting(columns);
}

/**
 * Settings resolution is two levels deep; a sub-group nested at depth ≥ 3 is
 * treated as an atomic unit (shown/hidden, pinned, and ordered by its own
 * `settingsKey ?? key`; inner structure kept as authored, not configurable).
 * Surface that degradation loudly in dev so deep configs are a choice, not an
 * accident. See "Column nesting depth" in `./table-view-state-versioning.md`.
 */
function warnDeepNesting<R>(columns: ColumnType<R>[]): void {
  for (const col of columns) {
    if (!isGroup(col) || isSystem(col)) continue;
    for (const child of col.children ?? []) {
      if (isGroup(child) && !isSystem(child)) {
        devWarn(`Sub-group "${child.key}" sits below the 2 levels settings resolution supports; `
          + 'it is treated as an atomic unit (toggled/pinned/ordered by its own key, inner structure not configurable).');
      }
    }
  }
}

function warnDuplicateKeys<R>(columns: ColumnType<R>[]): void {
  const seen = new Set<string>();
  const walk = (cols: ColumnType<R>[]): void => {
    for (const col of cols) {
      if (seen.has(col.key)) {
        devWarn(`Duplicate column key "${col.key}". Column keys must be unique.`);
      }
      seen.add(col.key);
      if (isGroup(col)) walk(col.children ?? []);
    }
  };
  walk(columns);
}

function warnSettingsKeyCollisions<R>(columns: ColumnType<R>[]): void {
  const parentByKey = new Map<string, string>();
  const walk = (cols: ColumnType<R>[], parent: string): void => {
    for (const col of cols) {
      if (isGroup(col)) {
        walk(col.children ?? [], col.key);
        continue;
      }
      if (isSystem(col)) continue;
      const key = stateKeyOf(col);
      const prev = parentByKey.get(key);
      if (prev !== undefined && prev !== parent) {
        devWarn(`settingsKey "${key}" spans multiple groups; panel grouping and ordering are ambiguous.`);
      } else if (prev === undefined) {
        parentByKey.set(key, parent);
      }
    }
  };
  walk(columns, '');
}

/**
 * Development-only: user state that pins a column declared `pinnable: false`.
 * The resolver ignores such pins (it clamps to `defaultPinned`), so this only
 * surfaces the config/state mismatch — it never changes the render. Never
 * throws; a no-op in production — the guard skips the walk entirely.
 */
export function warnNonPinnablePins<R>(
  columns: ColumnType<R>[],
  state: TableViewState | null | undefined,
): void {
  if (!isDevEnv()) return;
  if (!state || !Array.isArray(state.pinned) || state.pinned.length === 0) return;
  const nonPinnable = collectNonPinnableKeys(columns);
  const offenders = state.pinned.filter((key) => nonPinnable.has(key));
  if (offenders.length > 0) {
    devWarn(`Ignoring pin(s) on non-pinnable column(s): ${offenders.join(', ')}.`);
  }
}

function collectNonPinnableKeys<R>(columns: ColumnType<R>[]): Set<string> {
  const keys = new Set<string>();
  const visit = (col: ColumnType<R>, parent?: ColumnType<R>): void => {
    if (isSystem(col)) return;
    if (!effectivePinnable(col, parent)) keys.add(stateKeyOf(col));
  };
  for (const col of columns) {
    visit(col);
    if (isGroup(col) && !isSystem(col)) {
      (col.children ?? []).forEach((child) => visit(child, col));
    }
  }
  return keys;
}

/**
 * Development-only: state that references keys no settings unit exposes (a
 * removed column, a disabled feature flag, another tenant's plan — or a leaf
 * inside an atomic depth-≥3 sub-group, which is addressable only by the
 * sub-group's own key). Such keys are ignored on render and preserved on
 * write; this logs the ignore so it is visible. Never throws; a no-op in
 * production — the guard skips the walk entirely.
 */
export function warnStaleStateKeys<R>(
  columns: ColumnType<R>[],
  state: TableViewState | null | undefined,
): void {
  if (!isDevEnv()) return;
  if (!state) return;
  const known = collectStateKeys(columns);
  const stale = new Set<string>();
  const flag = (key: string): void => {
    if (!known.has(key)) stale.add(key);
  };
  (state.visible ?? []).forEach(flag);
  (state.pinned ?? []).forEach(flag);
  (state.order?.topLevel ?? []).forEach(flag);
  Object.entries(state.order?.children ?? {}).forEach(([groupKey, leafKeys]) => {
    flag(groupKey);
    leafKeys.forEach(flag);
  });
  if (stale.size > 0) {
    devWarn(`Ignoring ${stale.size} stale state key(s) matching no settings unit: ${[...stale].join(', ')}.`);
  }
}

/**
 * The keys the resolver actually consults: top-level units and the direct
 * children of top-level groups. Deliberately capped at the atomic boundary —
 * a depth-≥3 sub-group contributes its own key, never its descendants'.
 */
function collectStateKeys<R>(columns: ColumnType<R>[]): Set<string> {
  const keys = new Set<string>();
  for (const col of columns) {
    if (isSystem(col)) continue;
    keys.add(stateKeyOf(col));
    if (isGroup(col)) {
      for (const child of col.children ?? []) {
        if (isSystem(child)) continue;
        keys.add(stateKeyOf(child));
      }
    }
  }
  return keys;
}
